/**
 * Clinic Document Chat Component
 *
 * Chat interface for Q&A about clinic documents. Uses AG-UI protocol SSE streaming
 * to provide grounded answers with inline citations from uploaded documents.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown, { type Components, defaultUrlTransform } from 'react-markdown';
import { EmptyState, InputFooter, ScrollContainer } from '@components/ui/chat';
import { generateRunId } from '@features/ai-chat-core';
import { useMessageHover } from '@hooks/useMessageHover';
import {
  ArrowUpward as SendIcon,
  Delete as ClearIcon,
  Description as DocIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import {
  addUserMessage,
  clearError,
  type ClinicDocChatState,
  type DocToolCall,
  newConversation,
} from '../store/clinicDocChatSlice';
import { useStreamClinicDocChatQuery } from '../store/clinicDocStreamingApi';

import { ClinicDocumentFileViewer } from './ClinicDocumentFileViewer';

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Tool call activity indicator - shows "Searching Documents" during retrieval
 */
const SearchingDocsBubble: React.FC<{ toolCall: DocToolCall }> = React.memo(({ toolCall }) => {
  const theme = useTheme();

  const displayName = toolCall.toolCallName
    .replace(/^mcp__\w+__/, '')
    .replace(/_/g, ' ');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: 'transparent',
          background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <DocIcon sx={{ fontSize: 16, color: 'white' }} />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: 'transparent',
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <SearchIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            fontStyle: 'italic',
          }}
        >
          Searching documents{displayName !== 'unknown' ? ` (${displayName})` : ''}...
        </Typography>
        <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.text.secondary }} />
      </Paper>
    </Box>
  );
});
SearchingDocsBubble.displayName = 'SearchingDocsBubble';

/**
 * Thinking indicator bubble
 */
const ThinkingBubble: React.FC<{ message?: string }> = React.memo(({ message = 'Thinking...' }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
        '@keyframes fadeIn': {
          '0%': { opacity: 0, transform: 'translateY(10px)' },
          '100%': { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: 'transparent',
          background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <DocIcon sx={{ fontSize: 16, color: 'white' }} />
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          maxWidth: '75%',
          bgcolor: 'transparent',
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
          borderTopLeftRadius: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: '0.875rem',
            fontStyle: 'italic',
          }}
        >
          {message}
        </Typography>
        <CircularProgress size={14} thickness={4} sx={{ color: theme.palette.text.secondary }} />
      </Paper>
    </Box>
  );
});
ThinkingBubble.displayName = 'ThinkingBubble';

// ============================================================================
// Citation URL parsing
// ============================================================================

interface ParsedCiteUrl {
  clinicDocumentId: string;
  sectionId: string | null;
  page: number | null;
}

/**
 * Parses a cite: URL into its components.
 * Format: cite:{doc_id}/{section_id}#page={page_number}
 */
const parseCiteUrl = (href: string): ParsedCiteUrl | null => {
  if (!href.startsWith('cite:')) return null;

  const withoutScheme = href.slice('cite:'.length);
  const [pathPart, hashPart] = withoutScheme.split('#');
  const [docId, sectionId] = pathPart.split('/');

  if (!docId) return null;

  let page: number | null = null;
  if (hashPart) {
    const pageMatch = hashPart.match(/^page=(\d+)$/);
    if (pageMatch) {
      page = parseInt(pageMatch[1], 10);
    }
  }

  return {
    clinicDocumentId: docId,
    sectionId: sectionId || null,
    page,
  };
};

/**
 * Message bubble with markdown rendering for assistant messages.
 * Extracts and displays citations as badges below the message.
 */
const DocChatMessageBubble: React.FC<{
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
  onMouseEnter?: (id: string) => void;
  onMouseLeave?: () => void;
  onCitationClick?: (parsed: ParsedCiteUrl) => void;
}> = React.memo(({ id, role, content, isStreaming, onMouseEnter, onMouseLeave, onCitationClick }) => {
  const theme = useTheme();
  const isUser = role === 'user';

  const markdownComponents = useMemo<Components>(
    () => ({
      a: ({ href, children }) => {
        if (href) {
          const parsed = parseCiteUrl(href);
          if (parsed) {
            return (
              <Link
                component="button"
                variant="body2"
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  onCitationClick?.(parsed);
                }}
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 500,
                  color: 'primary.main',
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  cursor: 'pointer',
                  verticalAlign: 'baseline',
                  '&:hover': {
                    textDecorationStyle: 'solid',
                  },
                }}
              >
                {children}
              </Link>
            );
          }
        }
        return (
          <Link href={href} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ fontSize: 'inherit' }}>
            {children}
          </Link>
        );
      },
    }),
    [onCitationClick],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        animation: 'fadeIn 0.3s ease-in',
      }}
      onMouseEnter={() => onMouseEnter?.(id)}
      onMouseLeave={() => onMouseLeave?.()}
    >
      {!isUser && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: 'transparent',
            background: theme.palette.chat?.gradients?.ai || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}
        >
          <DocIcon sx={{ fontSize: 16, color: 'white' }} />
        </Avatar>
      )}

      <Box sx={{ maxWidth: '80%' }}>
        <Paper
          elevation={isUser ? 2 : 1}
          sx={{
            p: 1.5,
            bgcolor: isUser
              ? (theme.palette.chat?.colors?.userBubble || theme.palette.primary.main)
              : (theme.palette.chat?.colors?.messageBubbleBackground || '#f5f5f5'),
            color: isUser
              ? (theme.palette.chat?.colors?.userText || '#fff')
              : (theme.palette.chat?.colors?.assistantText || theme.palette.text.primary),
            borderRadius: 2,
            borderTopRightRadius: isUser ? 0 : 16,
            borderTopLeftRadius: isUser ? 16 : 0,
            boxShadow: isUser ? '0 2px 4px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.08)',
            border: isUser
              ? 'none'
              : `1px solid ${theme.palette.chat?.colors?.messageBubbleBorder || theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography
              variant="caption"
              sx={{
                color: isUser ? 'rgba(255,255,255,0.7)' : theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              {isUser ? 'You' : 'Document Assistant'}
            </Typography>
            {isStreaming && (
              <CircularProgress
                size={10}
                thickness={5}
                sx={{ ml: 1, color: theme.palette.text.secondary }}
              />
            )}
          </Box>

          {isUser ? (
            <Typography variant="body2" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              {content}
            </Typography>
          ) : (
            <Box
              sx={{
                '& p': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  m: '0.25em 0',
                  '&:first-of-type': { mt: 0 },
                  '&:last-of-type': { mb: 0 },
                },
                '& h1, & h2, & h3': {
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  lineHeight: 1.3,
                  m: '0.75em 0 0.25em',
                },
                '& strong': { fontWeight: 600 },
                '& code': {
                  fontSize: '0.8125rem',
                  fontFamily: 'monospace',
                  bgcolor: 'rgba(0,0,0,0.05)',
                  p: '0.125em 0.375em',
                  borderRadius: '3px',
                },
                '& pre': {
                  fontSize: '0.8125rem',
                  fontFamily: 'monospace',
                  bgcolor: 'rgba(0,0,0,0.05)',
                  p: 1.5,
                  borderRadius: 1,
                  overflow: 'auto',
                  my: 1,
                },
                '& ul, & ol': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  pl: 2.5,
                  my: 0.5,
                },
                '& li': {
                  fontSize: '0.875rem',
                  lineHeight: 1.6,
                  my: 0.25,
                },
                '& table': {
                  fontSize: '0.8125rem',
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 1,
                },
                '& th, & td': {
                  border: `1px solid ${theme.palette.divider}`,
                  p: '4px 8px',
                  textAlign: 'left',
                },
                '& th': {
                  bgcolor: 'rgba(0,0,0,0.03)',
                  fontWeight: 600,
                },
                '& blockquote': {
                  borderLeft: `3px solid ${theme.palette.divider}`,
                  pl: 1.5,
                  ml: 0,
                  color: theme.palette.text.secondary,
                  fontStyle: 'italic',
                },
              }}
            >
              <Markdown
                components={markdownComponents}
                urlTransform={url => (url.startsWith('cite:') ? url : defaultUrlTransform(url))}
              >
                {content}
              </Markdown>
            </Box>
          )}
        </Paper>
      </Box>

      {isUser && (
        <Avatar
          sx={{
            width: 28,
            height: 28,
            bgcolor: theme.palette.primary.main,
            fontSize: 14,
          }}
        >
          U
        </Avatar>
      )}
    </Box>
  );
});
DocChatMessageBubble.displayName = 'DocChatMessageBubble';

// ============================================================================
// Stable empty defaults
// ============================================================================

const emptyMessages: ClinicDocChatState['messages'] = [];
const emptyActivities: ClinicDocChatState['activities'] = {};
const emptyToolCalls: ClinicDocChatState['activeToolCalls'] = [];

// ============================================================================
// Main Component
// ============================================================================

export function ClinicDocumentChat() {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const { clinicDocuments: trackClinicDocs } = useEventTracking();

  const [input, setInput] = useState('');
  const [activeStream, setActiveStream] = useState<{
    threadId: string | null;
    runId: string;
    messages: { id: string; role: 'user'; content: string }[];
  } | null>(null);
  const [citationViewer, setCitationViewer] = useState<{
    clinicDocumentId: string;
    page: number | null;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { hoveredMessageId: _hoveredMessageId, handleMouseEnter, handleMouseLeave } = useMessageHover();

  // Redux state
  const chatState = useAppSelector(
    state =>
      (state as unknown as Record<string, ClinicDocChatState>)['clinicDocuments/chat'] as
        | ClinicDocChatState
        | undefined,
  );

  const messages = chatState?.messages ?? emptyMessages;
  const activities = chatState?.activities ?? emptyActivities;
  const assistantStatus = chatState?.assistant?.status ?? 'idle';
  const streamingContent = chatState?.assistant?.streamingContent ?? '';
  const firstTokenReceived = chatState?.assistant?.firstTokenReceived ?? false;
  const currentStep = chatState?.assistant?.currentStep ?? null;
  const error = chatState?.error ?? null;
  const threadId = chatState?.threadId ?? null;
  const activeToolCalls = chatState?.activeToolCalls ?? emptyToolCalls;

  // Trigger streaming via RTK Query
  useStreamClinicDocChatQuery(
    activeStream
      ? {
          threadId: activeStream.threadId,
          runId: activeStream.runId,
          messages: activeStream.messages,
        }
      : ({} as never),
    { skip: !activeStream },
  );

  const handleSend = useCallback(() => {
    if (!input.trim()) return;
    if (assistantStatus !== 'idle') return;

    const content = input.trim();
    dispatch(addUserMessage(content));
    setInput('');

    const runId = generateRunId();
    const messageId = crypto.randomUUID();

    trackClinicDocs.chatMessageSent({
      message_length: content.length,
      thread_id: threadId ?? undefined,
      message_count: messages.length + 1,
    });

    setActiveStream({
      threadId,
      runId,
      messages: [{ id: messageId, role: 'user', content }],
    });
  }, [input, dispatch, assistantStatus, threadId, trackClinicDocs, messages.length]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleNewConversation = useCallback(() => {
    trackClinicDocs.chatConversationCleared({
      message_count: messages.length,
      thread_id: threadId ?? undefined,
    });
    dispatch(newConversation());
    setActiveStream(null);
    inputRef.current?.focus();
  }, [dispatch, trackClinicDocs, messages.length, threadId]);

  const handleDismissError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleCitationClick = useCallback((parsed: ParsedCiteUrl) => {
    trackClinicDocs.chatCitationClicked({
      clinic_document_id: parsed.clinicDocumentId,
      page: parsed.page ?? undefined,
      section_id: parsed.sectionId ?? undefined,
    });
    setCitationViewer({
      clinicDocumentId: parsed.clinicDocumentId,
      page: parsed.page,
    });
  }, [trackClinicDocs]);

  // Track assistant response completion
  const prevStatusRef = useRef(assistantStatus);
  useEffect(() => {
    if (prevStatusRef.current === 'streaming' && assistantStatus === 'idle') {
      const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
      trackClinicDocs.chatResponseReceived({
        response_length: lastAssistantMsg?.content.length ?? 0,
        thread_id: threadId ?? undefined,
        message_count: messages.length,
      });
    }
    prevStatusRef.current = assistantStatus;
  }, [assistantStatus, messages, threadId, trackClinicDocs]);

  // Track chat errors
  const prevErrorRef = useRef(error);
  useEffect(() => {
    if (error && error !== prevErrorRef.current) {
      trackClinicDocs.chatError({
        error_message: error,
        thread_id: threadId ?? undefined,
      });
    }
    prevErrorRef.current = error;
  }, [error, threadId, trackClinicDocs]);

  // Build display items
  const displayItems = useMemo(() => {
    const items: React.ReactNode[] = [];

    messages.forEach(msg => {
      items.push(
        <DocChatMessageBubble
          key={msg.id}
          id={msg.id}
          role={msg.role}
          content={msg.content}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onCitationClick={handleCitationClick}
        />,
      );
    });

    // Show active tool calls (searching documents)
    activeToolCalls.forEach(tc => {
      items.push(<SearchingDocsBubble key={`tool-${tc.toolCallId}`} toolCall={tc} />);
    });

    // Show activity snapshots
    Object.values(activities).forEach(activity => {
      items.push(
        <ThinkingBubble
          key={`activity-${activity.messageId}`}
          message={activity.message}
        />,
      );
    });

    // Show thinking indicator
    if (
      assistantStatus === 'thinking' &&
      activeToolCalls.length === 0 &&
      Object.keys(activities).length === 0
    ) {
      items.push(
        <ThinkingBubble key="thinking" message={currentStep || 'Thinking...'} />,
      );
    }

    // Show streaming content
    /* eslint-disable jsx-a11y/aria-role */
    if (assistantStatus === 'streaming' && streamingContent && firstTokenReceived) {
      items.push(
        <DocChatMessageBubble
          key="streaming-temp"
          id="streaming-temp"
          role="assistant"
          content={streamingContent}
          isStreaming
          onCitationClick={handleCitationClick}
        />,
      );
      /* eslint-enable jsx-a11y/aria-role */
    }

    return items;
  }, [
    messages,
    activeToolCalls,
    activities,
    assistantStatus,
    streamingContent,
    firstTokenReceived,
    currentStep,
    handleMouseEnter,
    handleMouseLeave,
    handleCitationClick,
  ]);

  const isInputDisabled = assistantStatus !== 'idle';
  const hasMessages = messages.length > 0;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DocIcon sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Document Q&A
          </Typography>
          {threadId && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Conversation active
            </Typography>
          )}
        </Box>
        {hasMessages && (
          <Tooltip title="New conversation">
            <IconButton onClick={handleNewConversation} size="small" aria-label="New conversation">
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" onClose={handleDismissError} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}

      {/* Messages Container */}
      <ScrollContainer
        emptyState={
          <EmptyState
            icon={<DocIcon sx={{ fontSize: 28, color: 'white' }} />}
            title="Document Q&A"
            description="Ask questions about your clinic's uploaded documents. Get grounded answers with citations."
            examples={[
              'What are our reimbursement rates for CPT 99213?',
              'Summarize our referral policy',
              'What does our contract say about timely filing?',
            ]}
            onExampleClick={(example: string) => {
              setInput(example);
              inputRef.current?.focus();
            }}
          />
        }
      >
        {displayItems}
      </ScrollContainer>

      {/* Input Footer */}
      <InputFooter elevation>
        <Stack direction="row" spacing={1} sx={{alignItems: "flex-end",  width: '100%' }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder="Ask about your documents..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isInputDisabled}
            multiline
            minRows={2}
            maxRows={6}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
                borderRadius: 2,
                '& fieldset': { borderColor: '#d5d3e0' },
                '&:hover fieldset': { borderColor: '#ada1f3' },
                '&.Mui-focused fieldset': { borderColor: '#836eed', borderWidth: 2 },
                '&.Mui-disabled': { bgcolor: '#faf9fc' },
              },
              '& .MuiInputBase-input': { fontSize: 14, lineHeight: 1.5 },
            }}
          />
          <IconButton
            onClick={handleSend}
            disabled={!input.trim() || isInputDisabled}
            aria-label="Send message"
            sx={{
              bgcolor: '#4D32D7',
              color: 'white',
              opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
              '&:hover': {
                bgcolor: '#36229e',
                opacity: !input.trim() || isInputDisabled ? 0.3 : 1,
              },
              '&:disabled': { bgcolor: '#4D32D7', color: 'white', opacity: 0.3 },
              transition: 'all 0.2s ease',
              width: 40,
              height: 40,
            }}
          >
            <SendIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </Stack>
      </InputFooter>

      {/* Citation file viewer */}
      <ClinicDocumentFileViewer
        open={!!citationViewer}
        onClose={() => setCitationViewer(null)}
        clinicDocumentId={citationViewer?.clinicDocumentId ?? null}
        contentType="application/pdf"
        filename="document.pdf"
        initialPage={citationViewer?.page}
      />
    </Box>
  );
}
